/**
 * Product Collapsible Rows Block - JavaScript
 *
 * Provides expand/collapse functionality with:
 * - ARIA compliance (aria-expanded attribute management)
 * - Keyboard navigation support (Enter/Space keys)
 * - Smooth animations
 * - Event handling for toggle actions
 *
 * Based on existing collapsible-tabs.js pattern but adapted for product blocks
 */

if (!customElements.get("collapsible-rows")) {
  customElements.define(
    "collapsible-rows",
    class CollapsibleRows extends HTMLElement {
      constructor() {
        super();
        this.detailsElements = [];
      }

      connectedCallback() {
        // Get all details elements within this component
        this.detailsElements = this.querySelectorAll(".accordion-details");

        if (this.detailsElements.length > 0) {
          this.detailsElements.forEach((details) => {
            // Set initial ARIA state based on open attribute
            this.updateAriaState(details, details.open);

            // Listen for native toggle events
            details.addEventListener("toggle", this.onToggle.bind(this));

            // Add keyboard navigation support to summary elements
            const summary = details.querySelector(
              ".accordion-details__summary"
            );
            if (summary) {
              this.addKeyboardSupport(summary, details);
            }
          });
        }
      }

      /**
       * Handle toggle events when details element opens/closes
       * @param {Event} event - The toggle event
       */
      onToggle(event) {
        const details = event.target;
        this.updateAriaState(details, details.open);

        // Announce state change to screen readers
        this.announceStateChange(details);
      }

      /**
       * Update ARIA attributes for accessibility
       * @param {HTMLDetailsElement} details - The details element
       * @param {boolean} isOpen - Whether the details is open
       */
      updateAriaState(details, isOpen) {
        // Update aria-expanded on the details element
        details.setAttribute("aria-expanded", isOpen.toString());

        // Update summary element if it exists
        const summary = details.querySelector(".accordion-details__summary");
        if (summary) {
          summary.setAttribute("aria-expanded", isOpen.toString());
        }
      }

      /**
       * Add keyboard navigation support (Enter and Space keys)
       * @param {HTMLElement} summary - The summary element
       * @param {HTMLDetailsElement} details - The parent details element
       */
      addKeyboardSupport(summary, details) {
        // Prevent duplicate event listeners
        if (summary.dataset.keyboardEnabled) return;
        summary.dataset.keyboardEnabled = "true";

        summary.addEventListener("keydown", (event) => {
          // Check if Enter or Space key was pressed
          if (event.key === "Enter" || event.key === " ") {
            // Prevent default to avoid double-triggering
            event.preventDefault();

            // Toggle the details element
            details.open = !details.open;

            // Manually dispatch toggle event since we're toggling programmatically
            details.dispatchEvent(new Event("toggle"));
          }
        });
      }

      /**
       * Announce state change to screen readers
       * @param {HTMLDetailsElement} details - The details element
       */
      announceStateChange(details) {
        const summary = details.querySelector(".accordion-details__summary");
        if (!summary) return;

        // Get the heading text for announcement
        const heading = summary.querySelector("h2");
        const headingText = heading ? heading.textContent.trim() : "Section";

        // Create or update live region for screen reader announcements
        let liveRegion = details.querySelector(".sr-only-live-region");
        if (!liveRegion) {
          liveRegion = document.createElement("div");
          liveRegion.className = "sr-only-live-region";
          liveRegion.setAttribute("role", "status");
          liveRegion.setAttribute("aria-live", "polite");
          liveRegion.style.position = "absolute";
          liveRegion.style.width = "1px";
          liveRegion.style.height = "1px";
          liveRegion.style.margin = "-1px";
          liveRegion.style.padding = "0";
          liveRegion.style.overflow = "hidden";
          liveRegion.style.clip = "rect(0, 0, 0, 0)";
          liveRegion.style.whiteSpace = "nowrap";
          liveRegion.style.border = "0";
          details.appendChild(liveRegion);
        }

        // Update announcement text
        const state = details.open ? "expanded" : "collapsed";
        liveRegion.textContent = `${headingText} ${state}`;

        // Clear announcement after screen readers have processed it
        setTimeout(() => {
          liveRegion.textContent = "";
        }, 1000);
      }

      disconnectedCallback() {
        // Clean up event listeners when component is removed
        if (this.detailsElements.length > 0) {
          this.detailsElements.forEach((details) => {
            details.removeEventListener("toggle", this.onToggle.bind(this));
          });
        }
      }
    }
  );
}

/**
 * Enhanced keyboard navigation for accordion groups
 * Allows arrow key navigation between accordion items
 */
if (!customElements.get("accordion-group")) {
  customElements.define(
    "accordion-group",
    class AccordionGroup extends HTMLDetailsElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const summary = this.querySelector(".accordion-details__summary");
        if (summary) {
          // Add arrow key navigation
          summary.addEventListener("keydown", this.handleArrowKeys.bind(this));
        }
      }

      /**
       * Handle arrow key navigation between accordion items
       * @param {KeyboardEvent} event - The keyboard event
       */
      handleArrowKeys(event) {
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

        event.preventDefault();

        // Find all accordion items in the parent container
        const parent = this.closest(".accordion-parent");
        if (!parent) return;

        const allAccordions = Array.from(
          parent.querySelectorAll('[is="accordion-group"]')
        );
        const currentIndex = allAccordions.indexOf(this);

        let targetIndex;
        if (event.key === "ArrowUp") {
          // Move to previous accordion (wrap to last if at first)
          targetIndex =
            currentIndex > 0 ? currentIndex - 1 : allAccordions.length - 1;
        } else {
          // Move to next accordion (wrap to first if at last)
          targetIndex =
            currentIndex < allAccordions.length - 1 ? currentIndex + 1 : 0;
        }

        // Focus the target accordion's summary
        const targetAccordion = allAccordions[targetIndex];
        const targetSummary = targetAccordion.querySelector(
          ".accordion-details__summary"
        );
        if (targetSummary) {
          targetSummary.focus();
        }
      }
    },
    { extends: "details" }
  );
}
